import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import PageHeader from "../../components/PageHeader";
import SoftBox from "../../components/SoftBox";
import LoadingScreen from "../../components/LoadingScreen";
import { UseIsAdmin, UseUserDetails } from "../../hooks/auth";
import { UseCompanyColors, UseCompanyWorkspaces } from "../../hooks/company";
import { YETI_OUTLINE, YETI_PAGE, EXTRACT_DOCUMENT } from "../../api/playbook";
import { GET_PLAYBOOKS, CREATE_PLAYBOOK, DELETE_PLAYBOOK } from "./playbookStore";
import { GET_IMAGE } from "../../helpers/images";
import { textToHtml, htmlToSections } from "./importParse";
import YetiChat from "./YetiChat";
import "./playbook.css";

const Icon = ({ path, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {path}
  </svg>
);
const bookIcon = <><path d="M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 1-2-2Z" /><path d="M8 3v18" /></>;
const yetiStar = <path d="M12 3l2 4 4 .5-3 3 .8 4L12 12.7 8.2 14.5 9 10.5 6 7.5 10 7z" />;
const importIcon = <><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2Z" /></>;
const plusIcon = <path d="M12 5v14M5 12h14" />;
const penIcon = <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>;
const xIcon = <path d="M18 6 6 18M6 6l12 12" />;
const trashIcon = <><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>;

// Admins can create as many playbooks as they need; players open the one for
// their workspace.

// The workspace's default playbook (the pre-library "Handbook") is the one
// players open straight into and that can't be deleted. Until the backend adds
// an explicit `isDefault` flag, fall back to the type-less / "Handbook" record.
const isDefaultBook = (b) => !!b && (b.isDefault === true || !b.type || b.type === "Handbook");

// The single playbook a player should land on: an explicit default, else the
// "Handbook" record, else the first book shared with them.
const resolvePlayerBook = (list) => {
  if (!Array.isArray(list) || !list.length) return null;
  return (
    list.find((b) => b.isDefault === true) ||
    list.find((b) => !b.type || b.type === "Handbook") ||
    list[0]
  );
};

const PlaybookLibrary = () => {
  const navigate = useNavigate();
  const isAdmin = UseIsAdmin();
  const { groupId, image: userImage, firstName, lastName } = UseUserDetails();
  const { primaryColor } = UseCompanyColors();
  const workspaces = UseCompanyWorkspaces(true) || [];
  const wsName = (id) => workspaces.find((w) => String(w._id) === String(id))?.name || "Workspace";
  const [filter, setFilter] = useState("all");

  const [allBooks, setBooks] = useState(null);
  // Players (and admins viewing as a player) only see shared books. The
  // server cannot tell impersonation apart from a real admin session, so the
  // audience rules are applied here too.
  let books = allBooks;
  if (allBooks && !isAdmin) {
    books = allBooks.filter((b) => {
      const mode = b.audience?.mode;
      if (mode === "everyone") return true;
      if (mode === "workspaces")
        return (b.audience?.workspaceIds || []).map(String).includes(String(groupId));
      return false;
    });
  }
  const [modal, setModal] = useState(null); // null | 'choose' | 'generate' | 'import'
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [sourceText, setSourceText] = useState("");
  const [importTab, setImportTab] = useState("file"); // 'file' | 'paste'
  const [fileInfo, setFileInfo] = useState(null); // { name, size, pages } once extracted
  const [fileHtml, setFileHtml] = useState(null); // faithful rich HTML (docx)
  const [sourceEdited, setSourceEdited] = useState(false); // preview touched by hand
  const [extracting, setExtracting] = useState(false);
  const [importErr, setImportErr] = useState("");
  const fileInputRef = useRef(null);

  const prettySize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  };

  const resetImport = () => {
    setImportTab("file");
    setFileInfo(null);
    setFileHtml(null);
    setSourceEdited(false);
    setExtracting(false);
    setImportErr("");
    setSourceText("");
  };

  const handleFile = async (file) => {
    if (!file || extracting) return;
    setImportErr("");
    const ext = (file.name || "").toLowerCase().split(".").pop();
    if (!["pdf", "docx", "txt", "md"].includes(ext)) {
      setImportErr(ext === "doc" ? "Old Word format — save it as .docx and try again." : "Use a PDF, Word (.docx), or text file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setImportErr("That file is over the 10 MB limit.");
      return;
    }
    setExtracting(true);
    try {
      const res = await EXTRACT_DOCUMENT(file);
      setSourceText(res.data?.text || "");
      setFileHtml(res.data?.html || null);
      setSourceEdited(false);
      setFileInfo({ name: file.name, size: file.size, pages: res.data?.pages });
      if (!title.trim()) {
        const clean = file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim();
        setTitle(clean.charAt(0).toUpperCase() + clean.slice(1));
      }
    } catch (e) {
      setImportErr(e?.response?.data?.message || "Could not read that file — try again or paste the text.");
    } finally {
      setExtracting(false);
    }
  };
  const [busy, setBusy] = useState(false);
  // gen: { pct, stage, chapters:[{title,total,done}], done, total, active } while YETI writes
  const [gen, setGen] = useState(null);
  const [chOpen, setChOpen] = useState(false); // chapter drawer expanded
  const [cancelConfirm, setCancelConfirm] = useState(false); // "stop and discard?" showing
  const cancelRef = useRef(false); // flipped true to abort the write loop (discard)
  const [menuFor, setMenuFor] = useState(null); // book id with an open ••• menu
  const [delTarget, setDelTarget] = useState(null); // book pending delete-confirm
  const [deleting, setDeleting] = useState(false);

  // Any click outside closes the card menu.
  useEffect(() => {
    if (!menuFor) return undefined;
    const onDown = (e) => {
      if (!e.target.closest?.(".pb-kebab, .pb-card-menu")) {
        setMenuFor(null);
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [menuFor]);

  const deleteBook = async (id) => {
    setMenuFor(null);
    const target = (allBooks || []).find((b) => b._id === id);
    if (isDefaultBook(target)) {
      toast.error("The default playbook can't be deleted");
      setDelTarget(null);
      return;
    }
    const prev = books;
    setDeleting(true);
    setBooks((cur) => (cur || []).filter((b) => b._id !== id));
    try {
      await DELETE_PLAYBOOK(id);
      toast.success("Playbook deleted");
    } catch (e) {
      setBooks(prev);
      toast.error("Couldn't delete that playbook");
    } finally {
      setDeleting(false);
      setDelTarget(null);
    }
  };

  const load = () =>
    GET_PLAYBOOKS()
      .then((res) => setBooks(res.data || []))
      .catch(() => {
        setBooks([]);
        toast.error("Couldn't load playbooks");
      });

  useEffect(() => {
    load();
  }, []);

  // Players (real, not an admin viewing-as-player) don't browse a library —
  // they open their workspace's single playbook straight up.
  useEffect(() => {
    if (isAdmin || allBooks === null) return;
    const mine = resolvePlayerBook(books);
    if (mine?._id) navigate(`/playbook/${mine._id}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, allBooks]);

  const openBook = (id) => navigate(`/playbook/${id}`);

  const createBlank = async () => {
    setBusy(true);
    try {
      const res = await CREATE_PLAYBOOK({
        title: title || "New playbook",
        type: "Playbook",
        sections: [{ title: "Overview", order: 0, pages: [{ title: "Welcome", content: "", order: 0 }] }],
      });
      openBook(res.data._id);
    } catch (e) {
      toast.error("Couldn't create playbook");
      setBusy(false);
    }
  };

  // Import = the document, verbatim: its own headings become the chapters
  // and pages, wording and order untouched. YETI never runs here — the Ask
  // YETI orb in the editor is there if they want cleanup afterwards.
  const createFaithfulImport = async () => {
    setBusy(true);
    try {
      const bookTitle =
        title.trim() ||
        (fileInfo?.name ? fileInfo.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ") : "Imported document");
      // Word files arrive as faithful HTML; PDFs and pasted text are rebuilt
      // from the extracted lines. If the preview was edited by hand, the
      // edited text wins over the original rich HTML.
      const html = fileHtml && !sourceEdited ? fileHtml : textToHtml(sourceText);
      const sections = htmlToSections(html, bookTitle);
      const res = await CREATE_PLAYBOOK({ title: bookTitle, type: "Playbook", sections });
      const id = res.data?._id;
      toast.success("Imported — this is your document, ready to edit");
      setModal(null);
      if (id) navigate(`/playbook/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Import failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  // Tear down the build screen (shared by cancel + errors). Leaves no book.
  const stopGenerating = () => {
    cancelRef.current = true;
    setGen(null);
    setBusy(false);
    setChOpen(false);
    setCancelConfirm(false);
  };

  const createWithYeti = async ({ withSource, promptOverride }) => {
    cancelRef.current = false;
    setCancelConfirm(false);
    setChOpen(false);
    setBusy(true);
    setGen({ pct: 5, stage: "Learning your company", chapters: [], done: 0, total: 0, active: -1 });
    const src = withSource ? sourceText : undefined;
    try {
      // 1) Outline — sections + page titles. When YETI planned the book in a
      // conversation, that whole conversation is the prompt so the outline
      // reflects everything the admin clarified.
      const outlineRes = await YETI_OUTLINE({
        title: title || undefined,
        type: "Playbook",
        prompt: (promptOverride ?? prompt) || undefined,
        sourceText: src,
      });
      if (cancelRef.current) return; // discarded while outlining
      const outline = outlineRes.data || {};
      const sections = Array.isArray(outline.sections) ? outline.sections : [];
      const bookTitle = outline.title || title || "Company playbook";

      // Build the book skeleton + a flat page work-list.
      const built = sections.map((s, si) => ({
        title: s.title,
        order: si,
        pages: (s.pages || []).map((p, pi) => ({ title: p.title, content: "", order: pi })),
      }));
      const workList = [];
      built.forEach((s, si) =>
        s.pages.forEach((p, pi) => workList.push({ si, pi, sectionTitle: s.title, pageTitle: p.title }))
      );
      const total = workList.length || 1;
      const outlineText = built
        .map((s) => `${s.title}: ${s.pages.map((p) => p.title).join(", ")}`)
        .join("\n");

      // Per-chapter progress powers the (collapsed-by-default) chapter drawer.
      const chDone = built.map(() => 0);
      let done = 0;
      const snapshot = (pct, stage) => {
        const chapters = built.map((s, si) => ({ title: s.title, total: s.pages.length, done: chDone[si] }));
        const active = chapters.findIndex((c) => c.done < c.total);
        return { pct, stage, chapters, done, total, active };
      };

      setGen(snapshot(12, "Outlining chapters"));

      // 2) Write each page (small concurrency pool so it's fast + shows real %).
      const queue = [...workList];
      const writeOne = async (job) => {
        const res = await YETI_PAGE({
          bookTitle,
          sectionTitle: job.sectionTitle,
          pageTitle: job.pageTitle,
          outline: outlineText,
          sourceText: src,
        });
        if (cancelRef.current) return; // ignore late results after discard
        built[job.si].pages[job.pi].content = res.data?.content || "";
        done += 1;
        chDone[job.si] += 1;
        const active = built.findIndex((s, si) => chDone[si] < s.pages.length);
        const stage = active >= 0 ? `Writing “${built[active].title}”` : "Writing pages";
        setGen(snapshot(12 + Math.round((done / total) * 82), stage));
      };
      const worker = async () => {
        while (queue.length && !cancelRef.current) {
          const job = queue.shift();
          // eslint-disable-next-line no-await-in-loop
          await writeOne(job);
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, total) }, () => worker()));
      if (cancelRef.current) return; // discarded mid-write — never save

      // 3) Save the finished book.
      setGen(snapshot(97, "Saving your playbook"));
      const res = await CREATE_PLAYBOOK({ title: bookTitle, type: "Playbook", sections: built });
      if (cancelRef.current) return;
      setGen(snapshot(100, "Ready"));
      toast.success("YETI wrote your playbook");
      openBook(res.data._id);
    } catch (e) {
      if (cancelRef.current) return; // a discard racing with an error — stay quiet
      toast.error(e.message || "YETI couldn't generate that");
      setGen(null);
      setBusy(false);
    }
  };

  const closeModal = () => {
    if (busy) return;
    setModal(null);
    setTitle("");
    setPrompt("");
    setSourceText("");
  };

  const themeStyle = { "--pb-primary": primaryColor || "#1b598e" };

  // Chapters = <h1> headings across the book's content (continuous docs keep
  // their structure inside one page); falls back to the raw page count.
  const chapterCount = (b) => {
    let n = 0;
    (b.sections || []).forEach((s) =>
      (s.pages || []).forEach((p) => {
        n += (String(p.content || "").match(/<h1/g) || []).length;
      })
    );
    if (!n) n = (b.sections || []).reduce((t, s) => t + (s.pages || []).length, 0);
    return n;
  };

  // The "shared to" chip — names the workspace(s) so an admin sees where each
  // book lives at a glance. Everyone / a workspace name / "Sales +1" / Draft.
  const audienceChip = (b) => {
    const mode = b.audience?.mode;
    if (mode === "everyone") return { label: "Everyone", cls: "everyone" };
    if (mode === "workspaces") {
      const ids = b.audience?.workspaceIds || [];
      if (!ids.length) return { label: "Draft", cls: "draft" };
      const first = wsName(ids[0]);
      return { label: ids.length > 1 ? `${first} +${ids.length - 1}` : first, cls: "ws" };
    }
    return { label: "Draft", cls: "draft" };
  };

  // The three creation paths — shared by the empty state and the modal.
  const paths = [
    {
      key: "generate",
      icon: yetiStar,
      badge: "Fastest",
      h: "Generate with YETI",
      d: "Describe your company and YETI writes a full, on-brand handbook using your metrics.",
      onClick: () => setModal("generate"),
    },
    {
      key: "import",
      icon: importIcon,
      h: "Import a document",
      d: "Bring in a manual you already have — YETI turns it into a clean, structured playbook.",
      onClick: () => {
        resetImport();
        setModal("import");
      },
    },
    {
      key: "blank",
      icon: penIcon,
      h: "Start blank",
      d: "Open an empty book and write it your way — YETI is always one click away.",
      onClick: createBlank,
    },
  ];

  const playerBook = !isAdmin ? resolvePlayerBook(books) : null;
  let body;
  if (allBooks === null) {
    body = <LoadingScreen height="40vh" logoHeight="100px" />;
  } else if (!isAdmin) {
    // The redirect effect sends players straight to their playbook; show a
    // loader while that happens, or an empty state if their workspace has none.
    body = playerBook ? (
      <LoadingScreen height="40vh" logoHeight="100px" />
    ) : (
      <div className="pb-empty">
        <div className="pb-empty-icon"><Icon path={bookIcon} size={30} /></div>
        <h2 className="pb-empty-h">No playbook yet</h2>
        <p className="pb-empty-sub">Your admin hasn&apos;t published a playbook for your workspace yet. Check back soon.</p>
      </div>
    );
  } else if (books.length === 0 && isAdmin) {
    body = (
      <div className="pb-empty">
        <div className="pb-empty-icon"><Icon path={bookIcon} size={30} /></div>
        <h2 className="pb-empty-h">Create your first playbook</h2>
        <p className="pb-empty-sub">
          Pick how you want to start. You can add pages, edit anything by hand, and ask YETI anytime.
        </p>
        <div className="pb-empty-paths">
          {paths.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`pb-epath${p.badge ? " feat" : ""}${p.key === "generate" ? " pb-ai" : ""}`}
              onClick={p.onClick}
              disabled={busy}
            >
              {p.badge && <span className="pb-epath-badge">{p.badge}</span>}
              <span className="ic"><Icon path={p.icon} /></span>
              <div className="h">{p.h}</div>
              <div className="d">{p.d}</div>
            </button>
          ))}
        </div>
      </div>
    );
  } else if (books.length === 0) {
    body = (
      <div className="pb-empty">
        <div className="pb-empty-icon"><Icon path={bookIcon} size={30} /></div>
        <h2 className="pb-empty-h">No playbook yet</h2>
        <p className="pb-empty-sub">Your admin hasn&apos;t published a playbook yet. Check back soon.</p>
      </div>
    );
  } else {
    // Filter tabs — All / Everyone / each workspace with a book / Drafts.
    const isEveryone = (b) => b.audience?.mode === "everyone";
    const isDraft = (b) => !b.audience?.mode || b.audience?.mode === "private";
    const inWs = (b, id) =>
      b.audience?.mode === "workspaces" &&
      (b.audience?.workspaceIds || []).map(String).includes(String(id));
    const wsCounts = {};
    books.forEach((b) => {
      if (b.audience?.mode === "workspaces") {
        (b.audience?.workspaceIds || []).forEach((id) => {
          wsCounts[String(id)] = (wsCounts[String(id)] || 0) + 1;
        });
      }
    });
    const tabs = [{ key: "all", label: "All", n: books.length }];
    const evN = books.filter(isEveryone).length;
    if (evN) tabs.push({ key: "everyone", label: "Everyone", n: evN });
    Object.keys(wsCounts).forEach((id) => tabs.push({ key: id, label: wsName(id), n: wsCounts[id] }));
    const dfN = books.filter(isDraft).length;
    if (dfN) tabs.push({ key: "draft", label: "Drafts", n: dfN });
    const activeFilter = tabs.some((t) => t.key === filter) ? filter : "all";
    const shown = books.filter((b) => {
      if (activeFilter === "all") return true;
      if (activeFilter === "everyone") return isEveryone(b);
      if (activeFilter === "draft") return isDraft(b);
      return inWs(b, activeFilter);
    });
    body = (
      <>
        {isAdmin && tabs.length > 2 && (
          <div className="pb-filters">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`pb-fpill${activeFilter === t.key ? " on" : ""}`}
                onClick={() => setFilter(t.key)}
              >
                {t.label}<span className="pb-fpill-n">{t.n}</span>
              </button>
            ))}
          </div>
        )}
        <div className="pb-shelf">
          {shown.map((b) => {
          const c1 = b.theme?.primary || primaryColor || "#1b598e";
          const c2 = b.theme?.secondary || c1;
          const chapters = chapterCount(b);
          const chip = audienceChip(b);
          return (
            <div key={b._id} className="pb-bookcard" onClick={() => openBook(b._id)}>
              <div className="pb-jacket" style={{ "--bk1": c1, "--bk2": c2 }}>
                {isAdmin && (
                  <button
                    type="button"
                    className="pb-kebab"
                    aria-label="Playbook options"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuFor((cur) => (cur === b._id ? null : b._id));
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" />
                    </svg>
                  </button>
                )}
                <div className="pb-jacket-glyph"><Icon path={bookIcon} size={15} /></div>
                <div className="pb-jacket-title">{b.title || "Untitled"}</div>
                <div className="pb-jacket-pages">
                  {chapters > 0 ? `${chapters} chapter${chapters === 1 ? "" : "s"}` : "Empty"}
                </div>
                <span className="pb-jacket-edge" aria-hidden="true" />
              </div>
              {isAdmin && (
                <div className="pb-bookcard-meta">
                  <span className={`pb-aud-chip ${chip.cls}`}>{chip.label}</span>
                </div>
              )}
              {menuFor === b._id && (
                <div className="pb-card-menu" onClick={(e) => e.stopPropagation()}>
                  {isDefaultBook(b) ? (
                    <button type="button" className="danger" disabled style={{ opacity: 0.55, cursor: "default" }}>
                      <Icon path={bookIcon} size={14} />
                      Default playbook · can&apos;t be deleted
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="danger"
                      onClick={() => { setDelTarget(b); setMenuFor(null); }}
                    >
                      <Icon path={trashIcon} size={14} />
                      Delete playbook
                    </button>
                  )}
                </div>
              )}

            </div>
          );
        })}
        {isAdmin && (
          <div className="pb-bookcard" onClick={() => setModal("choose")}>
            <div className="pb-jacket new">
              <Icon path={plusIcon} size={20} />
              <div className="pb-jacket-new-t">Create a playbook</div>
            </div>
            <div className="pb-bookcard-meta">Generate, import, or start blank</div>
          </div>
        )}
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader headerText="Playbook" subheaderText="Your company handbooks — written with YETI or by hand" />
      <SoftBox className="pb-root" sx={{ px: { mini: 1, md: 3 }, pt: 2 }} style={themeStyle}>
        {body}
      </SoftBox>

      {modal && (
        <div className="pb-modal-scrim" onClick={closeModal}>
          <div
            className={`pb-root pb-modal${modal === "generate" ? " chat" : ""}`}
            style={themeStyle}
            onClick={(e) => e.stopPropagation()}
          >
            {gen && (
              <div className="pb-gen">
                <div className="pb-gen-head">
                  <div className="pb-gen-ava"><Icon path={yetiStar} size={22} /></div>
                  <div className="pb-gen-htext">
                    <div className="pb-gen-title">Building your playbook</div>
                    <div className="pb-gen-stage">{gen.pct >= 100 ? gen.stage : `${gen.stage}…`}</div>
                  </div>
                  <div className="pb-gen-pct">{gen.pct}%</div>
                </div>

                <div className="pb-gen-bar"><span style={{ width: `${gen.pct}%` }} /></div>

                {gen.chapters?.length > 0 && (
                  <>
                    <button
                      type="button"
                      className="pb-gen-toggle"
                      onClick={() => setChOpen((v) => !v)}
                      aria-expanded={chOpen}
                    >
                      <span className="pb-gen-pages">{gen.done} of {gen.total} pages</span>
                      <span className="pb-gen-chsum">
                        {gen.chapters.filter((c) => c.total > 0 && c.done >= c.total).length} of {gen.chapters.length} chapters
                        <svg className={`pb-gen-chev${chOpen ? " open" : ""}`} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                      </span>
                    </button>

                    <div className={`pb-gen-drawer${chOpen ? " open" : ""}`}>
                      <div className="pb-gen-chlist">
                        {gen.chapters.map((c, i) => {
                          const chDone = c.total > 0 && c.done >= c.total;
                          const chActive = !chDone && i === gen.active;
                          let chState = "pending";
                          if (chDone) chState = "done";
                          else if (chActive) chState = "active";
                          let chMark = i + 1;
                          if (chDone) {
                            chMark = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>;
                          } else if (chActive) {
                            chMark = <span className="pb-gen-spin" />;
                          }
                          return (
                            // Chapter order is stable/append-only.
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={i} className={`pb-gen-ch ${chState}`}>
                              <span className="pb-gen-chn">{chMark}</span>
                              <span className="pb-gen-cht">{c.title}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}

                <div className="pb-gen-foot">
                  {cancelConfirm ? (
                    <>
                      <span className="pb-gen-confirm">Stop and discard this draft?</span>
                      <div className="pb-gen-cbtns">
                        <button type="button" className="pb-gen-keep" onClick={() => setCancelConfirm(false)}>Keep writing</button>
                        <button type="button" className="pb-gen-stop" onClick={stopGenerating}>Stop</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="pb-gen-hint">YETI is writing your handbook</span>
                      <button type="button" className="pb-gen-cancel" onClick={() => setCancelConfirm(true)}>Cancel</button>
                    </>
                  )}
                </div>
              </div>
            )}

            {!gen && (
              <button type="button" className="pb-modal-x" onClick={closeModal} aria-label="Close">
                <Icon path={xIcon} size={18} />
              </button>
            )}

            {!gen && (
              <div className={`pb-modal-inner${modal === "generate" ? " chat" : ""}`}>
                {modal === "choose" && (
              <>
                <div className="pb-modal-h">Create a playbook</div>
                <div className="pb-modal-sub">Start from scratch, let YETI write it, or bring in a document you already have.</div>
                <div className="pb-paths">
                  {paths.map((p) => (
                    <div key={p.key} className={`pb-path${p.badge ? " feat" : ""}${p.key === "generate" ? " pb-ai" : ""}`} onClick={p.onClick}>
                      {p.badge && <span className="pb-epath-badge">{p.badge}</span>}
                      <span className="ic"><Icon path={p.icon} /></span>
                      <div className="h">{p.h}</div>
                      <div className="d">{p.d}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

                {modal === "generate" && (
                  <YetiChat
                    title={title}
                    setTitle={setTitle}
                    disabled={busy}
                    userAvatar={GET_IMAGE(userImage)}
                    userInitial={`${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "You"}
                    onBack={() => setModal("choose")}
                    onGenerate={(conversation) => createWithYeti({ withSource: false, promptOverride: conversation })}
                  />
                )}

                {modal === "import" && (
              <>
                <div className="pb-modal-h">Import a document</div>
                <div className="pb-modal-sub">
                  Drop in the handbook you already have — YETI rebuilds it as a clean, editable playbook.
                </div>
                <input
                  className="pb-field"
                  placeholder="Playbook title (e.g. Employee manual)"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                {modal === "import" && (
                  <>
                    <div className="pb-tabs">
                      <button
                        type="button"
                        className={importTab === "file" ? "on" : ""}
                        onClick={() => setImportTab("file")}
                      >
                        Upload a file
                      </button>
                      <button
                        type="button"
                        className={importTab === "paste" ? "on" : ""}
                        onClick={() => setImportTab("paste")}
                      >
                        Paste text
                      </button>
                    </div>

                    {importTab === "file" ? (
                      <>
                        {!fileInfo && (
                          <div
                            className={`pb-drop${extracting ? " busy" : ""}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => !extracting && fileInputRef.current.click()}
                            onKeyDown={(e) => e.key === "Enter" && fileInputRef.current.click()}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              handleFile(e.dataTransfer.files?.[0]);
                            }}
                          >
                            <span className="ic"><Icon path={importIcon} size={20} /></span>
                            <div className="h">{extracting ? "Reading your document…" : "Drop your handbook here"}</div>
                            <div className="d">
                              or <b>browse files</b> · PDF, Word or text · up to 10 MB
                            </div>
                          </div>
                        )}
                        <input
                          type="file"
                          accept=".pdf,.docx,.txt,.md"
                          ref={fileInputRef}
                          hidden
                          onChange={(e) => {
                            handleFile(e.target.files?.[0]);
                            e.target.value = "";
                          }}
                        />
                        {importErr && <div className="pb-import-err">{importErr}</div>}
                        {fileInfo && (
                          <>
                            <div className="pb-filechip">
                              <span className="ic"><Icon path={importIcon} size={15} /></span>
                              <span className="tx">
                                <span className="n">{fileInfo.name}</span>
                                <span className="s">
                                  {prettySize(fileInfo.size)}
                                  {fileInfo.pages ? ` · ${fileInfo.pages} pages` : ""} · text extracted
                                </span>
                              </span>
                              <button
                                type="button"
                                className="x"
                                aria-label="Remove file"
                                onClick={() => {
                                  setFileInfo(null);
                                  setFileHtml(null);
                                  setSourceText("");
                                }}
                              >
                                <Icon path={xIcon} size={13} />
                              </button>
                            </div>
                            <div className="pb-prevlabel">Preview · edit before building</div>
                            <textarea
                              className="pb-field pb-area tall"
                              value={sourceText}
                              onChange={(e) => {
                                setSourceText(e.target.value);
                                setSourceEdited(true);
                              }}
                            />
                          </>
                        )}
                      </>
                    ) : (
                      <textarea
                        className="pb-field pb-area tall"
                        placeholder="Paste your existing handbook text here…"
                        value={sourceText}
                        onChange={(e) => {
                          setSourceText(e.target.value);
                          setSourceEdited(true);
                        }}
                      />
                    )}
                  </>
                )}
                <div className="pb-modal-actions">
                  <button className="pb-btn ghost" onClick={() => setModal("choose")} disabled={busy}>Back</button>
                  <button
                    className="pb-btn primary"
                    disabled={busy || !sourceText.trim()}
                    onClick={() => createFaithfulImport()}
                  >
                    <Icon path={yetiStar} size={15} />
                    {busy ? "Importing…" : "Import document"}
                  </button>
                </div>
              </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {delTarget && (
        <div className="pb-root pb-modal-scrim" style={themeStyle} onClick={() => !deleting && setDelTarget(null)}>
          <div className="pb-modal confirm" onClick={(e) => e.stopPropagation()}>
            <div className="pb-modal-inner confirm">
              <div className="pb-confirm-ic"><Icon path={trashIcon} size={22} /></div>
              <div className="pb-confirm-h">Delete this playbook?</div>
              <div className="pb-confirm-sub">
                <b>{delTarget.title || "Untitled"}</b> and everything in it will be permanently removed. This can&apos;t be undone.
              </div>
              <div className="pb-confirm-actions">
                <button className="pb-btn ghost" onClick={() => setDelTarget(null)} disabled={deleting}>Cancel</button>
                <button className="pb-btn danger" onClick={() => deleteBook(delTarget._id)} disabled={deleting}>
                  {deleting ? "Deleting…" : "Delete playbook"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlaybookLibrary;
